import {
  assignWordsToIntervals,
  buildWordKey,
  type IntervalLike,
  type WordLike,
} from '@/lib/transcription-segments';

export interface PreviewTranscriptionWord extends WordLike {
  word: string;
  start: number;
  end: number;
  text?: string;
  punctuated_word?: string;
  _editId?: string;
  isWithinInterval?: boolean;
  gradientOpacity?: number;
  [key: string]: unknown;
}

export interface PreviewTranscriptionSegment {
  words: PreviewTranscriptionWord[];
  startTime: number;
  endTime: number;
  intervalStartTime: number;
}

const CONTEXT_BUFFER_SECONDS = 10;
const MAX_SEGMENT_WORDS = 30;
const STRATEGIC_BREAK_WORDS = 20;

const STOP_WORDS = new Set([
  'e', 'a', 'o', 'que', 'de', 'um', 'para', 'é', 'com', 'não', 'uma', 'os',
  'no', 'se', 'na', 'por', 'mais', 'as', 'do', 'tem', 'à', 'seu', 'sua', 'ou',
  'ser', 'quando', 'muito', 'há', 'nos', 'já', 'está', 'eu', 'também', 'só',
  'pelo', 'pela', 'até', 'isso', 'ela', 'entre', 'era', 'depois', 'sem',
  'mesmo', 'aos', 'ter', 'seus', 'suas', 'minha', 'meu', 'em', 'eles',
  'estão', 'você', 'tinha', 'foram', 'essa', 'esse', 'num', 'nem', 'meus',
  'minhas', 'numa', 'pelos', 'elas', 'havia', 'seja', 'qual', 'será', 'nós',
  'tenho', 'lhe', 'deles',
]);

const SENTENCE_ENDINGS = ['.', '!', '?', ';', ':', '...'];

const wordStart = (word: PreviewTranscriptionWord): number => (
  word.start || word.start_time || word.startTime || 0
);

const wordEnd = (word: PreviewTranscriptionWord): number => (
  word.end || word.end_time || word.endTime || wordStart(word)
);

const wordText = (word: PreviewTranscriptionWord): string => (
  word.word || word.text || ''
);

const hasSentenceEnding = (text: string): boolean => (
  SENTENCE_ENDINGS.some((ending) => text.includes(ending))
);

export function buildClipTranscriptionSegments({
  words,
  intervals,
  projectDuration,
}: {
  words: PreviewTranscriptionWord[];
  intervals: IntervalLike[];
  projectDuration: number;
}): PreviewTranscriptionSegment[][] {
  const wordToIntervalMap = assignWordsToIntervals(
    words,
    intervals,
    CONTEXT_BUFFER_SECONDS,
    projectDuration,
  );

  return intervals.map((interval, intervalIndex) => {
    const clipWords = words
      .filter((word) => (
        wordToIntervalMap.get(buildWordKey(word)) === intervalIndex
      ))
      .map((word) => {
        const startTime = wordStart(word);
        const endTime = wordEnd(word);
        const isWithinInterval = (
          (startTime >= interval.startTime && startTime <= interval.endTime)
          || (endTime >= interval.startTime && endTime <= interval.endTime)
          || (startTime <= interval.startTime && endTime >= interval.endTime)
        );

        let gradientOpacity = 1;
        if (!isWithinInterval) {
          let minimumDistance = Infinity;
          for (const candidate of intervals) {
            let distance = 0;
            if (startTime < candidate.startTime) {
              distance = candidate.startTime - startTime;
            } else if (startTime > candidate.endTime) {
              distance = startTime - candidate.endTime;
            }
            minimumDistance = Math.min(minimumDistance, distance);
          }
          const normalizedDistance = Math.min(
            minimumDistance / CONTEXT_BUFFER_SECONDS,
            1,
          );
          gradientOpacity = 0.5 - normalizedDistance * 0.45;
        }

        return {
          ...word,
          word: wordText(word),
          start: startTime,
          end: endTime,
          isWithinInterval,
          gradientOpacity,
        };
      });

    if (
      clipWords.length > 0
      && !clipWords.some((word) => word.isWithinInterval)
    ) {
      const intervalMidpoint = (interval.startTime + interval.endTime) / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      clipWords.forEach((word, index) => {
        const midpoint = (wordStart(word) + wordEnd(word)) / 2;
        const distance = Math.abs(midpoint - intervalMidpoint);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      clipWords[closestIndex] = {
        ...clipWords[closestIndex],
        isWithinInterval: true,
        gradientOpacity: 1,
      };
    }

    if (clipWords.length === 0) return [];

    const segments: PreviewTranscriptionSegment[] = [];
    let currentSegment: PreviewTranscriptionWord[] = [];
    let segmentStartTime = wordStart(clipWords[0]);

    for (let index = 0; index < clipWords.length; index += 1) {
      const word = clipWords[index];
      const text = wordText(word);
      const isLastWord = index === clipWords.length - 1;
      currentSegment.push(word);

      let shouldBreak = currentSegment.length >= MAX_SEGMENT_WORDS;
      if (hasSentenceEnding(text)) shouldBreak = true;

      if (
        currentSegment.length >= STRATEGIC_BREAK_WORDS
        && !shouldBreak
      ) {
        let goodBreakAhead = false;
        for (
          let lookahead = index + 1;
          lookahead < Math.min(index + 5, clipWords.length);
          lookahead += 1
        ) {
          const nextText = wordText(clipWords[lookahead]);
          if (
            hasSentenceEnding(nextText)
            || STOP_WORDS.has(nextText.toLowerCase())
          ) {
            goodBreakAhead = true;
            break;
          }
        }
        if (!goodBreakAhead) shouldBreak = true;
      }

      if (isLastWord) shouldBreak = true;
      if (!shouldBreak || currentSegment.length === 0) continue;

      segments.push({
        intervalStartTime: interval.startTime,
        words: currentSegment,
        startTime: segmentStartTime,
        endTime: wordEnd(word),
      });

      if (!isLastWord) {
        segmentStartTime = wordStart(clipWords[index + 1]);
        currentSegment = [];
      }
    }

    return segments;
  });
}
